import React, { useState, useMemo, useCallback, useEffect } from "react";
import Data from "../../../apiData/certificates.json";
import {
  Page, BgBlob, DotGrid, Inner,
  HeroWrap, HeroEyebrow, HeroTitle, HeroDivider, HeroDesc,
  FilterBar, FilterBtn,
  GalleryGrid, GalleryCard, CardImage,
  CategoryBadge, CATEGORY_COLORS,
  ModalOverlay, ModalImage, ModalClose,
  EmptyState,
} from "./GalleryPage.styles";

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Fisher-Yates shuffle — returns a new array, never mutates. */
function shuffleArray(array) {
  const out = [...array];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

// ─── Sub-components ───────────────────────────────────────────────────────────

const GalleryItem = ({ item, index, showBadge, onClick }) => (
  <GalleryCard
    $index={index}
    onClick={onClick}
    aria-label={`View certificate: ${item.title}`}
  >
    <CardImage src={item.link} alt={item.title} loading="lazy" />
    {showBadge && (
      <CategoryBadge $color={CATEGORY_COLORS[item.cat]}>
        🧘 {item.category}
      </CategoryBadge>
    )}
  </GalleryCard>
);
GalleryItem.displayName = "GalleryItem";

// ─────────────────────────────────────────────────────────────────────────────

const Lightbox = ({ src, onClose }) => {
  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  return (
    <ModalOverlay
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Certificate preview"
    >
      <ModalImage
        src={src}
        alt="Full view"
        onClick={(e) => e.stopPropagation()}
      />
      <ModalClose onClick={onClose} aria-label="Close preview">
        &times;
      </ModalClose>
    </ModalOverlay>
  );
};
Lightbox.displayName = "Lightbox";

// ─── GalleryPage ──────────────────────────────────────────────────────────────

const GalleryPage = () => {
  const [activeCategory, setActiveCategory] = useState(null);
  const [lightboxSrc,    setLightboxSrc]    = useState(null);

  // All unique categories, insertion-order preserved
  const categories = useMemo(
    () => [...new Set(Data.map((item) => item.category))],
    []
  );

  // Filtered items — shuffled so layout feels fresh on each filter change
  const items = useMemo(() => {
    const pool = activeCategory
      ? Data.filter((item) => item.category === activeCategory)
      : Data;
    return shuffleArray(pool);
  }, [activeCategory]);

  const handleFilter = useCallback((cat) => {
    setActiveCategory((prev) => (prev === cat ? null : cat));
  }, []);

  const openLightbox = useCallback((src) => {
    setLightboxSrc(src);
    document.body.style.overflow = "hidden";
  }, []);

  const closeLightbox = useCallback(() => {
    setLightboxSrc(null);
    document.body.style.overflow = "";
  }, []);

  return (
    <Page>
      <BgBlob className="b1" aria-hidden="true" />
      <BgBlob className="b2" aria-hidden="true" />
      <BgBlob className="b3" aria-hidden="true" />
      <DotGrid aria-hidden="true" />

      {/* ── Hero header ── */}
      <HeroWrap>
        <HeroEyebrow>✦ YogaShastra Gallery</HeroEyebrow>
        <HeroTitle>Certificates & Achievements</HeroTitle>
        <HeroDivider aria-hidden="true" />
        <HeroDesc>
          Browse our collection of yoga certificates and credentials — earned
          through dedicated practice and expert instruction.
        </HeroDesc>
      </HeroWrap>

      <Inner>
        {/* ── Category filter pills ── */}
        <FilterBar role="group" aria-label="Filter gallery by category">
          <FilterBtn
            $active={!activeCategory}
            onClick={() => setActiveCategory(null)}
            aria-pressed={!activeCategory}
          >
            All
          </FilterBtn>
          {categories.map((cat) => (
            <FilterBtn
              key={cat}
              $active={activeCategory === cat}
              onClick={() => handleFilter(cat)}
              aria-pressed={activeCategory === cat}
            >
              {cat}
            </FilterBtn>
          ))}
        </FilterBar>

        {/* ── Grid or empty state ── */}
        {items.length === 0 ? (
          <EmptyState>
            <span className="icon">🖼️</span>
            <h3>No certificates found</h3>
            <p>Try selecting a different category.</p>
          </EmptyState>
        ) : (
          <GalleryGrid>
            {items.map((item, index) => (
              <GalleryItem
                key={item.sN}
                item={item}
                index={index}
                showBadge={!activeCategory}
                onClick={() => openLightbox(item.link)}
              />
            ))}
          </GalleryGrid>
        )}
      </Inner>

      {/* ── Lightbox ── */}
      {lightboxSrc && (
        <Lightbox src={lightboxSrc} onClose={closeLightbox} />
      )}
    </Page>
  );
};

GalleryPage.displayName = "GalleryPage";
export default React.memo(GalleryPage);